import {
  Action,
  Actions,
  Card,
  Flags,
  openDeleteDialog,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {isEmpty, isNil} from 'ramda';

import {TelephonyGroup, TelephonyLine, useDeleteTelephonyGroupMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {
  buildArray,
  composePath,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useNavigate,
} from 'shared/web';

import {getRingStrategyLabel, getTelephonyGroupSummary} from '../utils/getTelephonyGroupSummary';

interface TelephonyGroupCardProps extends RequiredTestIdProps {
  group: TelephonyGroup;
  /**
   * Lines whose open target is this group; deleting is refused while any exist. Nullish while the
   * lines have not loaded, when usage is unknown and deleting is disabled.
   */
  usedByLines: TelephonyLine[] | Nullish;
  isReadOnly: boolean;
}

export function TelephonyGroupCard(props: TelephonyGroupCardProps) {
  const navigate = useNavigate();

  const [deleteGroup] = useDeleteTelephonyGroupMutation();

  const isUsageUnknown = isNil(props.usedByLines);
  const usedByLineNames = (props.usedByLines ?? []).map((line) => line.name).join(', ');

  const handleEdit = () =>
    navigate(composePath(settingsRoutes.telephonyGroupDetail, {params: {groupId: props.group.id}}));

  const handleDelete = () => {
    if (isNil(props.usedByLines)) {
      return;
    }

    // The backend refuses the delete with 409 while a line targets the group; say why up front.
    if (!isEmpty(props.usedByLines)) {
      showNotification.warning(
        i18n.t('page.settings.telephony.deleteGroupInUse', {lines: usedByLineNames})
      );
      return;
    }

    openDeleteDialog({
      text: i18n.t('page.settings.telephony.deleteGroupConfirm'),
      'data-testid': suffixTestId('deleteDialog', props),
      onConfirm: () =>
        deleteGroup({groupId: props.group.id})
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .catch(handleApiError),
    });
  };

  const actions = buildArray<Action>()
    .whenNot(props.isReadOnly, {
      type: 'iconButtonWithTooltip',
      icon: 'action/delete',
      tooltip: {label: i18n.t('general.actions.delete')},
      isDisabled: isUsageUnknown,
      onClick: handleDelete,
      'data-testid': suffixTestId('delete', props),
    })
    .add({
      type: 'iconButtonWithTooltip',
      icon: 'image/edit',
      tooltip: {label: i18n.t('general.actions.edit')},
      onClick: handleEdit,
      'data-testid': suffixTestId('edit', props),
    });

  return (
    <Card variant="inlineGrey" data-testid={suffixTestId('card', props)}>
      <VStack spacing={3}>
        <HStack justify="space-between" align="center" spacing={2}>
          <HStack spacing={2} align="center" wrap>
            <Text size="base" alternative data-testid={suffixTestId('name', props)}>
              {props.group.name}
            </Text>
            <Flags
              size="small"
              flags={[
                {
                  label: getRingStrategyLabel(props.group.ringStrategy),
                  colorScheme: props.group.ringStrategy === 'simultaneous' ? 'blue' : 'neutral',
                  isSubtle: true,
                },
              ]}
              data-testid={suffixTestId('strategy', props)}
            />
          </HStack>
          <Actions actions={actions} size="small" data-testid={suffixTestId('actions', props)} />
        </HStack>
        <HStack justify="space-between" align="center" spacing={2} wrap>
          <Text size="small" color="secondary" data-testid={suffixTestId('summary', props)}>
            {getTelephonyGroupSummary(props.group)}
          </Text>
          <Show when={!isUsageUnknown}>
            <Text size="xSmall" color="tertiary" data-testid={suffixTestId('usedBy', props)}>
              {isEmpty(props.usedByLines ?? [])
                ? i18n.t('page.settings.telephony.groupNotUsed')
                : i18n.t('page.settings.telephony.groupUsedByLines', {lines: usedByLineNames})}
            </Text>
          </Show>
        </HStack>
      </VStack>
    </Card>
  );
}
